import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import { BragCardCanvas } from './DesignStudio';
import PaginationBar from './PaginationBar';
import PrintExportModal from './PrintExportModal';
import { showToast } from '../context/ModalDialogContext';

// Safe copy to clipboard helper
const safeCopyToClipboard = async (text) => {
  if (!text) return false;
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('[Clipboard] writeText failed, fallback used', err);
    }
  }
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    textArea.setAttribute('readonly', '');
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return Boolean(successful);
  } catch (err) {
    console.warn('[Clipboard] execCommand fallback failed', err);
    return false;
  }
};

// SVG Icons
const Icons = {
  Vault: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="12" cy="12" r="5" />
      <path d="M12 7v2" /><path d="M12 15v2" /><path d="M7 12h2" /><path d="M15 12h2" />
    </svg>
  ),
  Eye: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Share: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
  Link: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Target: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  ),
  Copy: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  Check: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  ExternalLink: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  RotateCcw: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  Search: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  User: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Filter: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  Close: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Calendar: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Trophy: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.45 1-1 1H8v4h8v-4h-1c-.55 0-1-.45-1-1v-2.34" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
    </svg>
  ),
  Mail: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Globe: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Activity: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  Clock: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Printer: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  )
};

// Slick SVG Area & Line Chart Component for Timeline
const SlickTimelineChart = ({ timeline = [] }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!timeline || timeline.length === 0) {
    return (
      <div style={{ height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '11px' }}>
        No activity trend data recorded yet.
      </div>
    );
  }

  const width = 520;
  const height = 125;
  const paddingLeft = 30;
  const paddingRight = 14;
  const paddingTop = 14;
  const paddingBottom = 22;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const maxVal = Math.max(
    ...timeline.map(t => Math.max(t.views || 0, t.shares || 0, t.bookings || 0, t.clicks || 0)),
    5
  );

  const getX = (idx) => paddingLeft + (idx / Math.max(timeline.length - 1, 1)) * chartWidth;
  const getY = (val) => paddingTop + chartHeight - ((val || 0) / maxVal) * chartHeight;

  // Build SVG smooth path command
  const makePath = (dataKey) => {
    return timeline.reduce((acc, point, i) => {
      const x = getX(i);
      const y = getY(point[dataKey]);
      if (i === 0) return `M ${x} ${y}`;
      const prevX = getX(i - 1);
      const prevY = getY(timeline[i - 1][dataKey]);
      const cpX1 = prevX + (x - prevX) / 2;
      const cpX2 = prevX + (x - prevX) / 2;
      return `${acc} C ${cpX1} ${prevY}, ${cpX2} ${y}, ${x} ${y}`;
    }, '');
  };

  const viewsPath = makePath('views');
  const bookingsPath = makePath('bookings');
  const sharesPath = makePath('shares');

  const viewsArea = `${viewsPath} L ${getX(timeline.length - 1)} ${paddingTop + chartHeight} L ${getX(0)} ${paddingTop + chartHeight} Z`;

  return (
    <div style={{ position: 'relative', width: '100%', userSelect: 'none' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', overflow: 'visible', display: 'block' }}
      >
        <defs>
          <linearGradient id="vaultViewsGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00c6ff" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#00c6ff" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Horizontal grid lines */}
        {[0, 0.5, 1].map((ratio, idx) => {
          const y = paddingTop + chartHeight * (1 - ratio);
          return (
            <g key={idx}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke="rgba(255, 255, 255, 0.07)"
                strokeDasharray={ratio > 0 ? '3 3' : 'none'}
              />
              <text
                x={paddingLeft - 6}
                y={y + 3}
                fill="#94a3b8"
                fontSize="9"
                textAnchor="end"
                fontFamily="sans-serif"
              >
                {Math.round(maxVal * ratio)}
              </text>
            </g>
          );
        })}

        {/* Area fill under views line */}
        <path d={viewsArea} fill="url(#vaultViewsGradient)" />

        {/* Lines */}
        <path d={viewsPath} fill="none" stroke="#00c6ff" strokeWidth="2.4" strokeLinecap="round" />
        <path d={sharesPath} fill="none" stroke="#a855f7" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="4 2" />
        <path d={bookingsPath} fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" />

        {/* Data points & hover triggers */}
        {timeline.map((point, idx) => {
          const x = getX(idx);
          const yViews = getY(point.views);
          const isHovered = hoveredIdx === idx;
          return (
            <g key={idx}>
              {/* Vertical guideline on hover */}
              {isHovered && (
                <line
                  x1={x}
                  y1={paddingTop}
                  x2={x}
                  y2={paddingTop + chartHeight}
                  stroke="rgba(255, 255, 255, 0.25)"
                  strokeDasharray="2 2"
                />
              )}

              {/* View dot */}
              <circle
                cx={x}
                cy={yViews}
                r={isHovered ? 4.5 : 2.5}
                fill="#00c6ff"
                stroke="#111622"
                strokeWidth="1.5"
              />

              {/* Booking dot */}
              <circle
                cx={x}
                cy={getY(point.bookings)}
                r={isHovered ? 4 : 2}
                fill="#10b981"
                stroke="#111622"
                strokeWidth="1"
              />

              {/* Transparent hover target */}
              <rect
                x={x - chartWidth / (timeline.length * 2)}
                y={0}
                width={chartWidth / timeline.length}
                height={height}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              />

              {/* X Axis Labels (every 2-3 points) */}
              {(idx % Math.ceil(timeline.length / 6) === 0 || idx === timeline.length - 1) && (
                <text
                  x={x}
                  y={height - 8}
                  fill="#cbd5e1"
                  fontSize="9"
                  textAnchor="middle"
                  fontFamily="sans-serif"
                >
                  {point.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Floating Tooltip */}
      {hoveredIdx !== null && timeline[hoveredIdx] && (
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: `${Math.min(Math.max((hoveredIdx / (timeline.length - 1)) * 100, 15), 85)}%`,
            transform: 'translateX(-50%)',
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(0, 198, 255, 0.4)',
            borderRadius: '6px',
            padding: '5px 10px',
            fontSize: '11px',
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            zIndex: 10,
            whiteSpace: 'nowrap'
          }}
        >
          <div style={{ color: '#f8fafc', fontWeight: 700, marginBottom: '3px' }}>
            {timeline[hoveredIdx].label} ({timeline[hoveredIdx].date})
          </div>
          <div style={{ display: 'flex', gap: '8px', color: '#cbd5e1' }}>
            <span style={{ color: '#38bdf8' }}>👁️ {timeline[hoveredIdx].views} Views</span>
            <span style={{ color: '#c084fc' }}>↗️ {timeline[hoveredIdx].shares} Shares</span>
            <span style={{ color: '#34d399' }}>🎯 {timeline[hoveredIdx].bookings} Bookings</span>
          </div>
        </div>
      )}

      {/* Chart Legend */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginTop: '4px', fontSize: '10px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#38bdf8' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#00c6ff' }} />
          Views
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#c084fc' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#a855f7' }} />
          Shares
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#34d399' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#10b981' }} />
          Bookings / Clicks
        </span>
      </div>
    </div>
  );
};

// Helper for Log Event Category and Visual Styling
const getLogEventMeta = (eventType) => {
  switch (eventType) {
    case 'click_booking':
      return {
        icon: Icons.Target,
        color: '#10b981',
        bg: 'rgba(16, 185, 129, 0.15)',
        badge: 'Booking Click',
        badgeBg: 'rgba(16, 185, 129, 0.15)',
        badgeColor: '#34d399',
        defaultDesc: 'Operator booking inquiry link clicked from card'
      };
    case 'click_website':
      return {
        icon: Icons.Globe,
        color: '#38bdf8',
        bg: 'rgba(56, 189, 248, 0.15)',
        badge: 'Website Click',
        badgeBg: 'rgba(56, 189, 248, 0.15)',
        badgeColor: '#38bdf8',
        defaultDesc: 'Official operator website visited from card'
      };
    case 'click_email':
      return {
        icon: Icons.Mail,
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.15)',
        badge: 'Email Click',
        badgeBg: 'rgba(245, 158, 11, 0.15)',
        badgeColor: '#fbbf24',
        defaultDesc: 'Operator email inquiry contact initiated'
      };
    case 'shared_view':
      return {
        icon: Icons.Share,
        color: '#a855f7',
        bg: 'rgba(168, 85, 247, 0.15)',
        badge: 'Shared View',
        badgeBg: 'rgba(168, 85, 247, 0.15)',
        badgeColor: '#c084fc',
        defaultDesc: 'Card opened from a shared link / social export'
      };
    case 'share':
      return {
        icon: Icons.Share,
        color: '#ec4899',
        bg: 'rgba(236, 72, 153, 0.15)',
        badge: 'Card Shared',
        badgeBg: 'rgba(236, 72, 153, 0.15)',
        badgeColor: '#f472b6',
        defaultDesc: 'Trophy card shared via WhatsApp, Facebook, or QR'
      };
    case 'view':
    default:
      return {
        icon: Icons.Eye,
        color: '#00c6ff',
        bg: 'rgba(0, 198, 255, 0.15)',
        badge: 'Direct Scan',
        badgeBg: 'rgba(0, 198, 255, 0.15)',
        badgeColor: '#00c6ff',
        defaultDesc: 'Physical coin NFC chip tapped or scanned directly'
      };
  }
};

// Formats event timestamp into human-readable relative time
const formatEventTime = (isoString) => {
  if (!isoString) return 'Recent';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return 'Recent';
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return `Yesterday ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return 'Recent';
  }
};

// Helper: Safely resolve the claim email for allocated coins across various schema conventions
export function getCoinClaimEmail(coin) {
  if (!coin || !coin.is_issued) return null;
  if (coin.claim_email && typeof coin.claim_email === 'string') return coin.claim_email.trim();
  if (coin.trophy_owner_email && typeof coin.trophy_owner_email === 'string') return coin.trophy_owner_email.trim();
  const cd = coin.card_data || {};
  const direct = cd.trophy_owner_email || cd.trophy_owner_mail || cd.angler_email || cd.hunter_email || cd.owner_email || cd.trophy_hunter_email || cd.recipient_email || cd.user_email || cd.email;
  if (direct && typeof direct === 'string' && direct.includes('@')) {
    return direct.trim();
  }
  if (cd.trophy_owner && typeof cd.trophy_owner === 'object') {
    const nested = cd.trophy_owner.email || cd.trophy_owner.trophy_owner_email;
    if (nested && typeof nested === 'string' && nested.includes('@')) {
      return nested.trim();
    }
  }
  for (const [k, v] of Object.entries(cd)) {
    const kLower = k.toLowerCase();
    if (kLower.includes('operator') || kLower.includes('org') || kLower.includes('system') || kLower.includes('admin') || kLower.includes('logger')) continue;
    if (kLower.includes('email') || kLower.includes('mail')) {
      if (typeof v === 'string' && v.includes('@')) return v.trim();
    }
  }
  return null;
}

// 3D Flippable Card Widget for the Modal
const VaultCardPreview = ({ coin, isFlipped, onFlip, user, tenantConfig, isAdmin, onPrintExport }) => {
  const template = coin?.card_template_snapshot || {};
  const isAllocated = Boolean(coin?.is_issued);

  // If allocated, use actual card data. If unallocated, cardData is completely blank!
  const cardData = isAllocated ? (coin?.card_data || {}) : {};

  // Extract dimensions from template or fallback to standard 400x560
  const templateConfig = template?.config || template || {};
  const cardDimensions = templateConfig.cardDimensions || template.cardDimensions || { width: 400, height: 560 };
  const cardWidth = cardDimensions.width || 400;
  const cardHeight = cardDimensions.height || 560;

  // Proportional scale factor so card fits cleanly into the preview column
  const targetWidth = 320;
  const scale = targetWidth / cardWidth;
  const scaledWidth = targetWidth;
  const scaledHeight = Math.round(cardHeight * scale);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '380px' }}>
      {/* Tap Card to Flip hint */}
      <div style={{
        fontSize: '11px',
        color: '#cbd5e1',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '8px',
        cursor: 'pointer',
        userSelect: 'none'
      }} onClick={onFlip}>
        <Icons.RotateCcw size={12} />
        <span>Tap card or click button below to flip ({isFlipped ? 'Back' : 'Front'})</span>
      </div>

      {/* Allocated DOM box matching exact scaled dimensions */}
      <div
        style={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          position: 'relative',
          margin: '0 auto',
          flexShrink: 0
        }}
      >
        {/* Scaled coordinate container */}
        <div
          onClick={onFlip}
          style={{
            width: `${cardWidth}px`,
            height: `${cardHeight}px`,
            position: 'absolute',
            top: 0,
            left: 0,
            transform: scale !== 1 ? `scale(${scale})` : undefined,
            transformOrigin: 'top left',
            perspective: '1200px',
            WebkitPerspective: '1200px',
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              transformStyle: 'preserve-3d',
              WebkitTransformStyle: 'preserve-3d',
              position: 'relative',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              WebkitTransform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front Side */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(0deg) translateZ(1px)',
                WebkitTransform: 'rotateY(0deg) translateZ(1px)',
                transformStyle: 'flat',
                WebkitTransformStyle: 'flat',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)',
                zIndex: isFlipped ? 1 : 2,
                pointerEvents: isFlipped ? 'none' : 'auto'
              }}
            >
              <BragCardCanvas
                design={template}
                side="front"
                testDataOverrides={cardData}
                datasets={{}}
                user={user}
                tenantConfig={tenantConfig}
                id={`vault-coin-front-${coin.id}`}
              />
            </div>

            {/* Back Side */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg) translateZ(1px)',
                WebkitTransform: 'rotateY(180deg) translateZ(1px)',
                transformStyle: 'flat',
                WebkitTransformStyle: 'flat',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)',
                zIndex: isFlipped ? 2 : 1,
                pointerEvents: isFlipped ? 'auto' : 'none'
              }}
            >
              <BragCardCanvas
                design={template}
                side="back"
                testDataOverrides={cardData}
                datasets={{}}
                user={user}
                tenantConfig={tenantConfig}
                id={`vault-coin-back-${coin.id}`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Flip Button & Admin Print Export Action */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={onFlip}
          style={{
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            color: '#cbd5e1',
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)'}
        >
          <Icons.RotateCcw size={13} />
          <span>Flip to {isFlipped ? 'Front' : 'Back'}</span>
        </button>

        {isAdmin && onPrintExport && (
          <button
            type="button"
            data-testid="vault-card-preview-print-btn"
            onClick={onPrintExport}
            style={{
              background: 'rgba(234, 179, 8, 0.12)',
              border: '1px solid rgba(234, 179, 8, 0.35)',
              color: '#fef08a',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(234, 179, 8, 0.22)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(234, 179, 8, 0.12)'}
            title="Export high-resolution Epson print PDF"
          >
            <Icons.Printer size={13} style={{ color: '#facc15' }} />
            <span>Export to Print</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Main CoinVaultTab Component
export default function CoinVaultTab({
  user,
  tenantConfig,
  isGlobalRole,
  isAdminRole,
  tenants = [],
  selectedTenantId,
  setSelectedTenantId
}) {
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isAdmin = Boolean(isAdminRole || isGlobalRole || user?.role === 'GLOBAL_ADMIN' || user?.role === 'TENANT_ADMIN');

  // Tenant Scope State (Global Admin can toggle tenants, Tenant Admin scoped to own tenant)
  const [activeTenantId, setActiveTenantId] = useState(
    selectedTenantId || (isGlobalRole ? '' : (tenantConfig?.id ? tenantConfig.id.toString() : ''))
  );

  // Sync activeTenantId if parent passes new selectedTenantId
  useEffect(() => {
    if (selectedTenantId !== undefined && selectedTenantId !== null) {
      setActiveTenantId(selectedTenantId);
    }
  }, [selectedTenantId]);

  // Handler for global admin switching tenant
  const handleTenantChange = (newTenantId) => {
    setActiveTenantId(newTenantId);
    setSelectedOperatorId(''); // Reset operator when switching tenant
    if (setSelectedTenantId) {
      setSelectedTenantId(newTenantId);
    }
  };

  // State
  const [loading, setLoading] = useState(true);
  const [vaultData, setVaultData] = useState({
    total_coins: 0,
    allocated_coins: 0,
    unallocated_coins: 0,
    total_views: 0,
    total_shares: 0,
    total_shared_views: 0,
    total_booking_clicks: 0,
    total_website_clicks: 0,
    total_email_clicks: 0,
    total_host_clicks: 0,
    coins: []
  });

  // Filters & Controls
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'allocated', 'unallocated'
  const [selectedOperatorId, setSelectedOperatorId] = useState(''); // '' means all operators
  const [operatorsList, setOperatorsList] = useState([]);

  // Process operatorsList into consolidated organisation / solo operator scopes
  const displayScopes = useMemo(() => {
    const orgMap = new Map();
    const soloOperators = [];

    for (const op of operatorsList) {
      const org = (op.organization_name || op.company_name || '').trim();
      if (org) {
        // Group by organization ID or lowercase organization name
        const key = op.organization_id ? `id_${op.organization_id}` : `name_${org.toLowerCase()}`;
        if (orgMap.has(key)) {
          const existing = orgMap.get(key);
          existing.total_coins = (existing.total_coins || 0) + (op.total_coins || 0);
          if (Array.isArray(existing.userIds)) {
            if (op.id && !existing.userIds.includes(op.id)) existing.userIds.push(op.id);
          } else {
            existing.userIds = [existing.id, op.id].filter(Boolean);
          }
        } else {
          orgMap.set(key, {
            ...op,
            displayName: org,
            total_coins: op.total_coins || 0,
            userIds: op.id ? [op.id] : []
          });
        }
      } else {
        const userName = (
          op.name ||
          (op.first_name || op.last_name ? `${op.first_name || ''} ${op.last_name || ''}`.trim() : '') ||
          op.email ||
          'Unknown Operator'
        ).trim();
        soloOperators.push({
          ...op,
          displayName: userName,
          total_coins: op.total_coins || 0,
          userIds: op.id ? [op.id] : []
        });
      }
    }

    const result = [...orgMap.values(), ...soloOperators];
    result.sort((a, b) => {
      if ((b.total_coins || 0) !== (a.total_coins || 0)) {
        return (b.total_coins || 0) - (a.total_coins || 0);
      }
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
    return result;
  }, [operatorsList]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Pro-tip display (shown once only per user)
  const proTipStorageKey = `vault_protip_seen_${user?.id || user?.email || 'default'}`;
  const [showProTip, setShowProTip] = useState(() => {
    try {
      return !localStorage.getItem(proTipStorageKey);
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      const seen = localStorage.getItem(proTipStorageKey);
      if (seen) {
        setShowProTip(false);
      }
    } catch (e) {}
  }, [proTipStorageKey]);

  useEffect(() => {
    if (showProTip) {
      try {
        localStorage.setItem(proTipStorageKey, 'true');
      } catch (e) {}
    }
  }, [proTipStorageKey, showProTip]);

  const handleDismissProTip = () => {
    setShowProTip(false);
    try {
      localStorage.setItem(proTipStorageKey, 'true');
    } catch (e) {}
  };

  // Modal State
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [coinAnalytics, setCoinAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [logFilter, setLogFilter] = useState('all'); // 'all', 'views', 'clicks', 'shares'
  const [isMobileModal, setIsMobileModal] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth <= 860 : false;
  });

  // Print Export State
  const [showPrintExportModal, setShowPrintExportModal] = useState(false);
  const [isPrintExporting, setIsPrintExporting] = useState(false);
  const [printExportStep, setPrintExportStep] = useState('');
  const [printExportError, setPrintExportError] = useState('');
  const [printExportSuccess, setPrintExportSuccess] = useState(false);

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (printExportSuccess) showToast('Print export PDF generated successfully!', 'success');
  }, [printExportSuccess]);

  useEffect(() => {
    if (printExportError) showToast(printExportError, 'error');
  }, [printExportError]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileModal(window.innerWidth <= 860);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoized Event Logs
  const allLogs = useMemo(() => {
    return coinAnalytics?.logs || [];
  }, [coinAnalytics]);

  const logCounts = useMemo(() => {
    const views = allLogs.filter(l => l.event_type === 'view' || l.event_type === 'shared_view').length;
    const clicks = allLogs.filter(l => l.event_type === 'click_booking' || l.event_type === 'click_website' || l.event_type === 'click_email').length;
    const shares = allLogs.filter(l => l.event_type === 'share').length;
    return { all: allLogs.length, views, clicks, shares };
  }, [allLogs]);

  const filteredLogs = useMemo(() => {
    if (logFilter === 'all') return allLogs;
    if (logFilter === 'views') {
      return allLogs.filter(l => l.event_type === 'view' || l.event_type === 'shared_view');
    }
    if (logFilter === 'clicks') {
      return allLogs.filter(l => l.event_type === 'click_booking' || l.event_type === 'click_website' || l.event_type === 'click_email');
    }
    if (logFilter === 'shares') {
      return allLogs.filter(l => l.event_type === 'share');
    }
    return allLogs;
  }, [allLogs, logFilter]);

  // Fetch Operators for Scope Switcher (Tenant Admins & Global Admins)
  useEffect(() => {
    if (!token) return;
    const fetchOperators = async () => {
      try {
        const queryTenant = isGlobalRole ? activeTenantId : (user?.tenant_id || tenantConfig?.id || activeTenantId || '');
        const url = `${API_BASE}/api/v1/coin-vault/vault/operators${queryTenant ? `?tenant_id=${queryTenant}` : ''}`;
        const res = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok) {
          const data = await res.json();
          setOperatorsList(data.operators || []);
        }
      } catch (err) {
        console.warn('[CoinVault] Failed to load operators list:', err);
      }
    };

    if (isAdminRole || isGlobalRole || user?.role === 'TENANT_ADMIN') {
      fetchOperators();
    }
  }, [token, isAdminRole, isGlobalRole, user, tenantConfig, activeTenantId, API_BASE]);

  // Debounce search query
  useEffect(() => {
    if (searchQuery === debouncedSearch) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, selectedOperatorId, activeTenantId, sortBy, debouncedSearch]);

  // Fetch Coins from Vault
  const fetchVaultCoins = async (pageOverride, sizeOverride) => {
    if (!token) return;
    setLoading(true);
    try {
      const pageToUse = pageOverride !== undefined ? pageOverride : currentPage;
      const sizeToUse = sizeOverride !== undefined ? sizeOverride : pageSize;

      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (selectedOperatorId) {
        params.append('operator_id', selectedOperatorId);
        const matchedScope = displayScopes.find(s => s.id?.toString() === selectedOperatorId || (s.userIds && s.userIds.map(String).includes(selectedOperatorId)));
        if (matchedScope?.organization_id) {
          params.append('organization_id', matchedScope.organization_id);
        }
      }
      if (isGlobalRole && activeTenantId) params.append('tenant_id', activeTenantId);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('page', pageToUse);
      params.append('limit', sizeToUse);

      const url = `${API_BASE}/api/v1/coin-vault/vault?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        setVaultData(data);
      }
    } catch (err) {
      console.error('[CoinVault] Error fetching vault coins:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVaultCoins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, selectedOperatorId, activeTenantId, sortBy, debouncedSearch, currentPage, pageSize]);

  // Fetch Analytics for Selected Coin in Modal
  useEffect(() => {
    if (!selectedCoin || !token) return;
    if (!selectedCoin.is_issued) {
      setCoinAnalytics(null);
      return;
    }

    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/v1/coin-vault/${selectedCoin.public_code}/analytics?days=14`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok) {
          const data = await res.json();
          setCoinAnalytics(data);
        }
      } catch (err) {
        console.warn('[CoinVault] Failed to fetch coin analytics:', err);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, [selectedCoin, token, API_BASE]);

  // Lock body scrolling and listen for Escape key when modal is open
  useEffect(() => {
    if (!selectedCoin) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showPrintExportModal) {
          setShowPrintExportModal(false);
        } else {
          setSelectedCoin(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCoin, showPrintExportModal]);

  // Handle Double-Click on Coin Entry
  const handleDoubleClickCoin = (coin) => {
    setSelectedCoin(coin);
    setIsFlipped(false);
    setLogFilter('all');
    setShowPrintExportModal(false);
    setPrintExportError('');
    setPrintExportSuccess(false);
  };

  const handleExecuteVaultPrintExport = async (printOptions) => {
    if (!selectedCoin) return;
    setIsPrintExporting(true);
    setPrintExportError('');
    setPrintExportSuccess(false);
    setPrintExportStep('Preparing high-resolution card faces & original master assets...');

    // Helpers to clean canvas and HTML for print export so card edges are perfectly clean
    const prepareCanvasClean = (el) => {
      if (!el) return () => {};
      const prevShadow = el.style.boxShadow;
      const prevTransform = el.style.transform;
      const prevTransition = el.style.transition;
      el.style.boxShadow = 'none';
      el.style.transform = 'none';
      el.style.transition = 'none';
      return () => {
        el.style.boxShadow = prevShadow;
        el.style.transform = prevTransform;
        el.style.transition = prevTransition;
      };
    };

    const sanitizePrintHtml = (html) => {
      if (!html) return '';
      // Target only root card canvas container styles to neutralize outer scale/shadow,
      // strictly preserving all child element transforms (rotations, scales) and effects.
      return html.replace(
        /(<div[^>]*id=["'](?:vault-print-canvas-[^"']+|vault-coin-[^"']+|catch-card-canvas)["'][^>]*style=["'])([^"']*)(["'])/gi,
        (match, prefix, styleStr, suffix) => {
          let cleanStyle = styleStr
            .replace(/box-shadow:\s*[^;]+;?/gi, '')
            .replace(/transform:\s*scale\([^)]+\);?/gi, '')
            .replace(/transform:\s*matrix[^;]+;?/gi, '')
            .replace(/transition:\s*[^;]+;?/gi, '');
          cleanStyle = `box-shadow: none !important; transform: none !important; transition: none !important; margin: 0 !important; ${cleanStyle}`;
          return `${prefix}${cleanStyle}${suffix}`;
        }
      );
    };

    try {
      const clientScale = (printOptions && printOptions.dpi >= 600) ? 4.0 : 3.0;

      // Locate clean staging canvases (or fallback to preview canvases)
      const frontCanvas = document.getElementById(`vault-print-canvas-front-${selectedCoin.id}`) ||
                          document.getElementById(`vault-coin-front-${selectedCoin.id}`);
      const backCanvas = document.getElementById(`vault-print-canvas-back-${selectedCoin.id}`) ||
                          document.getElementById(`vault-coin-back-${selectedCoin.id}`);

      // 1. Capture Front Card Face
      setPrintExportStep('Rendering front face with uncompressed master assets...');
      const restoreFront = prepareCanvasClean(frontCanvas);
      await new Promise(resolve => setTimeout(resolve, 80));
      const frontHtml = sanitizePrintHtml(frontCanvas ? frontCanvas.outerHTML : '');
      let frontImage = null;
      try {
        if (frontCanvas && typeof html2canvas === 'function') {
          const cv = await html2canvas(frontCanvas, {
            scale: clientScale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,
            logging: false,
            onclone: (clonedDoc) => {
              const allNodes = clonedDoc.querySelectorAll('*');
              allNodes.forEach(node => {
                if (node.style) {
                  // Only neutralize outer scale/shadow on root card canvas containers
                  if (node.id && (node.id.startsWith('vault-print-canvas') || node.id.startsWith('vault-coin-') || node.id === 'catch-card-canvas')) {
                    node.style.boxShadow = 'none';
                    node.style.transform = 'none';
                    node.style.margin = '0';
                  }
                  // Neutralize 3D flip card rotation on flip wrappers without wiping out 2D child element rotations
                  if (node.style.transform && (node.style.transform.includes('rotateY') || node.style.transform.includes('matrix3d'))) {
                    node.style.transform = 'none';
                    node.style.webkitTransform = 'none';
                  }
                  if (node.style.perspective) node.style.perspective = 'none';
                  if (node.style.backfaceVisibility) node.style.backfaceVisibility = 'visible';
                }
              });
            }
          });
          frontImage = cv.toDataURL('image/png');
        }
      } catch (fErr) {
        console.warn('html2canvas front capture error:', fErr);
      } finally {
        restoreFront();
      }

      // 2. Capture Back Card Face
      setPrintExportStep('Rendering back face & calculating fold alignment...');
      const restoreBack = prepareCanvasClean(backCanvas);
      await new Promise(resolve => setTimeout(resolve, 80));
      const backHtml = sanitizePrintHtml(backCanvas ? backCanvas.outerHTML : '');
      let backImage = null;
      try {
        if (backCanvas && typeof html2canvas === 'function') {
          const cv = await html2canvas(backCanvas, {
            scale: clientScale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,
            logging: false,
            onclone: (clonedDoc) => {
              const allNodes = clonedDoc.querySelectorAll('*');
              allNodes.forEach(node => {
                if (node.style) {
                  // Only neutralize outer scale/shadow on root card canvas containers
                  if (node.id && (node.id.startsWith('vault-print-canvas') || node.id.startsWith('vault-coin-') || node.id === 'catch-card-canvas')) {
                    node.style.boxShadow = 'none';
                    node.style.transform = 'none';
                    node.style.margin = '0';
                  }
                  // Neutralize 3D flip card rotation on flip wrappers without wiping out 2D child element rotations
                  if (node.style.transform && (node.style.transform.includes('rotateY') || node.style.transform.includes('matrix3d'))) {
                    node.style.transform = 'none';
                    node.style.webkitTransform = 'none';
                  }
                  if (node.style.perspective) node.style.perspective = 'none';
                  if (node.style.backfaceVisibility) node.style.backfaceVisibility = 'visible';
                }
              });
            }
          });
          backImage = cv.toDataURL('image/png');
        }
      } catch (bErr) {
        console.warn('html2canvas back capture error:', bErr);
      } finally {
        restoreBack();
      }

      // 3. Request Backend High-DPI PDF Generation
      setPrintExportStep('Compiling Epson EcoTank L8160 PDF with crop marks & registration...');
      const template = selectedCoin?.card_template_snapshot || {};
      const templateConfig = template?.config || template || {};
      const cardDimensions = templateConfig.cardDimensions || template.cardDimensions || { width: 400, height: 560 };
      const cardName = printOptions.card_name || selectedCoin.card_name || template.name || `Coin_${selectedCoin.public_code || selectedCoin.id}`;

      const payload = {
        ...printOptions,
        design_id: selectedCoin.card_template_id || template.id || null,
        card_name: cardName,
        order_id: selectedCoin.order_id || null,
        order_number: printOptions.order_number || selectedCoin?.order?.order_number || (selectedCoin?.order_id ? String(selectedCoin.order_id) : null),
        coin_code: printOptions.coin_code || selectedCoin.public_code || null,
        card_width: cardDimensions.width || 400,
        card_height: cardDimensions.height || 560,
        front_html: frontHtml,
        back_html: backHtml,
        front_image: frontImage,
        back_image: backImage,
        use_original_assets: true
      };

      const res = await fetch(`${API_BASE}/api/v1/designs/export-print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.detail || 'Failed to generate print PDF.');
      }

      const blob = await res.blob();
      if (typeof window !== 'undefined' && window.URL && typeof window.URL.createObjectURL === 'function') {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeName = (payload.card_name || 'Trophy_Card').replace(/[^a-zA-Z0-9_-]/g, '_');
        a.download = `${safeName}_Epson_L8160_Print.pdf`;
        document.body.appendChild(a);
        a.click();
        if (typeof window.URL.revokeObjectURL === 'function') {
          window.URL.revokeObjectURL(url);
        }
        document.body.removeChild(a);
      }

      setPrintExportSuccess(true);
    } catch (err) {
      console.error('Vault print export error:', err);
      setPrintExportError(err.message || 'Failed to generate print PDF.');
    } finally {
      setIsPrintExporting(false);
    }
  };

  const renderPrintExportModal = () => {
    if (!showPrintExportModal || !selectedCoin) return null;

    const template = selectedCoin?.card_template_snapshot || {};
    const templateConfig = template?.config || template || {};
    const cardDimensions = templateConfig.cardDimensions || template.cardDimensions || { width: 400, height: 560 };
    const coinCardName = selectedCoin.card_name || template.name || (selectedCoin.public_code ? `Coin_${selectedCoin.public_code}` : 'Trophy Card');

    const coinOrderNumber = selectedCoin?.order?.order_number || (selectedCoin?.order_id ? String(selectedCoin.order_id) : '');
    const coinPublicCode = selectedCoin?.public_code || '';

    const modalContent = (
      <PrintExportModal
        isOpen={showPrintExportModal}
        onClose={() => {
          if (!isPrintExporting) {
            setShowPrintExportModal(false);
          }
        }}
        cardName={coinCardName}
        cardDimensions={cardDimensions}
        orderNumber={coinOrderNumber}
        coinCode={coinPublicCode}
        onGenerate={handleExecuteVaultPrintExport}
        isProcessing={isPrintExporting}
        processingStep={printExportStep}
        error={printExportError}
        success={printExportSuccess}
        onReset={() => {
          setPrintExportError('');
          setPrintExportSuccess(false);
        }}
      />
    );

    return typeof document !== 'undefined' && document.body
      ? createPortal(modalContent, document.body)
      : modalContent;
  };

  const handleCopy = async (text, id) => {
    const ok = await safeCopyToClipboard(text);
    if (ok) {
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  // Client search filtering (fallback if backend pagination/search not used)
  const filteredCoins = useMemo(() => {
    const rawCoins = vaultData.coins || [];
    if (!searchQuery.trim() || vaultData.total_filtered !== undefined) {
      return rawCoins;
    }
    const q = searchQuery.toLowerCase().trim();
    return rawCoins.filter(c => {
      if (c.public_code?.toLowerCase().includes(q)) return true;
      if (String(c.coin_number).includes(q)) return true;
      if (String(c.order_id).includes(q)) return true;
      if (c.card_name?.toLowerCase().includes(q)) return true;
      if (c.operator_name?.toLowerCase().includes(q)) return true;
      for (const v of Object.values(c.card_data || {})) {
        if (String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [vaultData.coins, vaultData.total_filtered, searchQuery]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Header & Title Banner */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: '16px',
        width: '100%'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minWidth: 0,
          flex: '1 1 auto',
          overflow: 'hidden'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '22px',
            fontWeight: 800,
            color: 'var(--primary-color)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            <Icons.Vault size={24} style={{ color: '#00c6ff' }} />
            {user?.role === 'END_USER' ? 'Personal Vault' : 'The Vault'}
          </h2>
          <span
            title={user?.role === 'END_USER' ? 'View your authentic trophy cards, 3D proofing, and digital awards.' : 'Manage minted coins, track public impressions & link shares, monitor booking click-throughs, and review 3D proofing.'}
            style={{
              fontSize: '13px',
              color: 'var(--text-mute, #64748b)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              paddingLeft: '2px'
            }}
          >
            — {user?.role === 'END_USER' ? 'View your authentic trophy cards, 3D proofing, and digital awards.' : 'Manage minted coins, track public impressions & link shares, monitor booking click-throughs, and review 3D proofing.'}
          </span>
        </div>

        {/* Scope Switcher (Tenant Users & Global Users) */}
        {(isAdminRole || isGlobalRole || user?.role === 'TENANT_ADMIN') && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: '12px',
            flexShrink: 0,
            marginLeft: 'auto',
            background: 'var(--card-bg, rgba(255, 255, 255, 0.04))',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '6px 14px'
          }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
              Scope:
            </span>

            {/* Tenant Scope Dropdown (Global Users Only) */}
            {isGlobalRole && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                <Icons.Globe size={14} style={{ color: '#0284c7', flexShrink: 0 }} />
                <label style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 600 }}>Tenant:</label>
                <select
                  value={activeTenantId}
                  onChange={(e) => handleTenantChange(e.target.value)}
                  aria-label="Tenant Scope"
                  style={{
                    background: 'var(--input-bg, #1e293b)',
                    border: '1px solid var(--input-border, rgba(255, 255, 255, 0.2))',
                    color: 'var(--input-text, var(--text-main))',
                    borderRadius: '6px',
                    padding: '5px 10px',
                    fontSize: '12px',
                    fontWeight: 500,
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="" style={{ background: 'var(--input-bg, #1e293b)', color: 'var(--input-text, var(--text-main))' }}>All Tenants (Global)</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id.toString()} style={{ background: 'var(--input-bg, #1e293b)', color: 'var(--input-text, var(--text-main))' }}>
                      {t.brand_name && t.domain_name && t.brand_name.toLowerCase() !== t.domain_name.toLowerCase() ? `${t.brand_name} (${t.domain_name})` : (t.brand_name || t.domain_name)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Operator Scope Dropdown (Both Global and Tenant Users) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              <Icons.User size={14} style={{ color: '#a855f7', flexShrink: 0 }} />
              <label style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 600 }}>Operator:</label>
              <select
                value={(() => {
                  if (!selectedOperatorId) return '';
                  const found = displayScopes.find(s => s.id?.toString() === selectedOperatorId || (s.userIds && s.userIds.map(String).includes(selectedOperatorId)));
                  return found ? found.id?.toString() : selectedOperatorId;
                })()}
                onChange={(e) => setSelectedOperatorId(e.target.value)}
                aria-label="Operator Scope"
                style={{
                  background: 'var(--input-bg, #1e293b)',
                  border: '1px solid var(--input-border, rgba(255, 255, 255, 0.2))',
                  color: 'var(--input-text, var(--text-main))',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="" style={{ background: 'var(--input-bg, #1e293b)', color: 'var(--input-text, var(--text-main))' }}>
                  {isGlobalRole && !activeTenantId ? 'All Operators (Global Scope)' : 'All Operators (Entire Tenant)'}
                </option>
                {displayScopes.map(op => {
                  return (
                    <option key={op.id} value={op.id.toString()} style={{ background: 'var(--input-bg, #1e293b)', color: 'var(--input-text, var(--text-main))' }}>
                      {op.displayName} — {op.total_coins} {op.total_coins === 1 ? 'coin' : 'coins'}
                    </option>
                  );
                })}
                {displayScopes.length === 0 && (
                  <option value="" disabled style={{ background: 'var(--input-bg, #1e293b)', color: 'var(--text-mute)' }}>
                    (No operators registered)
                  </option>
                )}
              </select>
            </div>
          </div>
        )}

        {/* Operator Badge for individual operators */}
        {user?.role === 'OPERATOR' && !(isAdminRole || isGlobalRole) && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(0, 198, 255, 0.12)',
            border: '1px solid rgba(0, 198, 255, 0.3)',
            color: '#38bdf8',
            borderRadius: '20px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 700,
            flexShrink: 0,
            marginLeft: 'auto',
            whiteSpace: 'nowrap'
          }}>
            <Icons.User size={13} />
            <span>My Vault ({user?.first_name || user?.email})</span>
          </div>
        )}
      </div>

      {/* 2. Top Summary KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px',
        width: '100%'
      }}>
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Total Minted
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-main)', marginTop: '4px' }}>
            {vaultData.total_coins}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
            Coins in vault
          </div>
        </div>

        <div style={{
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '10px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '11px', color: '#059669', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Allocated
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#059669', marginTop: '4px' }}>
            {vaultData.allocated_coins}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', fontWeight: 500 }}>
            {vaultData.total_coins > 0 ? Math.round((vaultData.allocated_coins / vaultData.total_coins) * 100) : 0}% issued
          </div>
        </div>

        <div style={{
          background: 'rgba(217, 119, 6, 0.08)',
          border: '1px solid rgba(217, 119, 6, 0.3)',
          borderRadius: '10px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '11px', color: '#d97706', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Unallocated
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#d97706', marginTop: '4px' }}>
            {vaultData.unallocated_coins}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', fontWeight: 500 }}>
            Available to issue
          </div>
        </div>

        <div style={{
          background: 'rgba(2, 132, 199, 0.08)',
          border: '1px solid rgba(2, 132, 199, 0.3)',
          borderRadius: '10px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '11px', color: '#0284c7', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Public Views
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#0284c7', marginTop: '4px' }}>
            {vaultData.total_views}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', fontWeight: 500 }}>
            {vaultData.total_shared_views} via shared links
          </div>
        </div>

        <div style={{
          background: 'rgba(147, 51, 234, 0.08)',
          border: '1px solid rgba(147, 51, 234, 0.3)',
          borderRadius: '10px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '11px', color: '#9333ea', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Total Shares
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#9333ea', marginTop: '4px' }}>
            {vaultData.total_shares}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', fontWeight: 500 }}>
            Viewer viral reach
          </div>
        </div>

        <div style={{
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '10px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '11px', color: '#059669', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Host Clicks
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#059669', marginTop: '4px' }}>
            {vaultData.total_host_clicks}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', fontWeight: 500 }}>
            {vaultData.total_booking_clicks} booking inquiries
          </div>
        </div>
      </div>

      {/* 3. Controls Bar: Status Filter Tabs, Search & Sort */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '10px',
        padding: '10px 14px'
      }}>
        {/* Status Filter Tabs */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            style={{
              background: statusFilter === 'all' ? 'var(--primary-color)' : 'transparent',
              color: statusFilter === 'all' ? '#ffffff' : 'var(--text-main)',
              border: statusFilter === 'all' ? '1px solid transparent' : '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            All Coins ({vaultData.total_coins})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('allocated')}
            style={{
              background: statusFilter === 'allocated' ? '#10b981' : 'transparent',
              color: statusFilter === 'allocated' ? '#ffffff' : '#059669',
              border: statusFilter === 'allocated' ? '1px solid transparent' : '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            ✓ Allocated ({vaultData.allocated_coins})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('unallocated')}
            style={{
              background: statusFilter === 'unallocated' ? '#d97706' : 'transparent',
              color: statusFilter === 'unallocated' ? '#ffffff' : '#d97706',
              border: statusFilter === 'unallocated' ? '1px solid transparent' : '1px solid rgba(217, 119, 6, 0.4)',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            ⏳ Unallocated ({vaultData.unallocated_coins})
          </button>
        </div>

        {/* Search & Sort Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Search Input */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--input-bg)',
            border: '1px solid var(--input-border, var(--border-color))',
            borderRadius: '6px',
            padding: '4px 10px',
            width: '220px'
          }}>
            <Icons.Search size={14} style={{ color: 'var(--text-mute)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search token, angler, species..."
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--input-text, var(--text-main))',
                fontSize: '12px',
                outline: 'none',
                width: '100%'
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-mute)', cursor: 'pointer', padding: 0 }}
              >
                <Icons.Close size={12} />
              </button>
            )}
          </div>

          {/* Sort Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 600 }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                background: 'var(--input-bg, var(--card-bg))',
                border: '1px solid var(--input-border, var(--border-color))',
                color: 'var(--input-text, var(--text-main))',
                borderRadius: '6px',
                padding: '5px 8px',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="views">Most Views</option>
              <option value="shares">Most Shares</option>
              <option value="clicks">Most Clicks</option>
              <option value="coin_number">Coin #</option>
            </select>
          </div>
        </div>
      </div>

      {/* Double-Click Instruction Banner (Shown once only to user) */}
      {showProTip && (
        <div style={{
          fontSize: '11px',
          color: 'var(--text-main)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          background: 'rgba(2, 132, 199, 0.08)',
          border: '1px dashed rgba(2, 132, 199, 0.35)',
          borderRadius: '6px',
          padding: '8px 12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>💡</span>
            <span><strong>Pro-tip:</strong> Double-click any coin row to open its interactive 3D flippable card preview and full analytics breakdown.</span>
          </div>
          <button
            onClick={handleDismissProTip}
            title="Dismiss pro-tip"
            aria-label="Dismiss pro-tip"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-mute)',
              padding: '2px 4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              flexShrink: 0,
              transition: 'color 0.15s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-main)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-mute)'}
          >
            <Icons.Close size={13} />
          </button>
        </div>
      )}

      {/* 4. Vault Coins Table */}
      <div style={{
        background: 'var(--card-bg, var(--panel-bg))',
        border: '1px solid var(--border-color)',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-mute)', fontSize: '13px' }}>
            Loading vaulted coins...
          </div>
        ) : filteredCoins.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-mute)' }}>
            <Icons.Vault size={36} style={{ color: 'var(--text-mute)', opacity: 0.7, marginBottom: '12px' }} />
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
              {user?.role === 'END_USER' ? 'No Trophy Cards Found' : 'No Coins Found'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '4px' }}>
              {user?.role === 'END_USER'
                ? 'No trophy cards have been linked to your account email yet. Once an operator logs your card, it will appear here in your personal vault.'
                : (statusFilter !== 'all' ? `No ${statusFilter} coins match your current search or filters.` : 'No coins have been minted into this vault yet.')
              }
            </div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'rgba(128, 128, 128, 0.08)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-main)' }}>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Coin</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Template / Title</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)', minWidth: '140px', whiteSpace: 'nowrap' }}>Status</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Views</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Shares</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Host Clicks</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Operator</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'right', color: 'var(--text-main)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoins.map((coin) => {
                  const isAllocated = coin.is_issued;
                  return (
                    <tr
                      key={coin.id}
                      data-testid={`coin-row-${coin.id}`}
                      onDoubleClick={() => handleDoubleClickCoin(coin)}
                      style={{
                        borderBottom: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(128, 128, 128, 0.06)'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {/* Coin & Token Column */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            background: 'rgba(2, 132, 199, 0.12)',
                            border: '1px solid rgba(2, 132, 199, 0.35)',
                            color: 'var(--primary-color, #0284c7)',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            fontWeight: 700
                          }}>
                            #{coin.coin_number}
                          </span>
                          <div>
                            <code style={{
                              fontSize: '11px',
                              color: 'var(--text-main)',
                              background: 'rgba(128, 128, 128, 0.12)',
                              border: '1px solid var(--border-color)',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              fontFamily: 'monospace'
                            }}>
                              {coin.public_code}
                            </code>
                            <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '2px' }}>
                              Order #{coin.order_id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Template / Title Column */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                          {coin.card_name}
                        </div>
                        {isAllocated && coin.card_data?.trophy_owner_name && (
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                            Angler: <strong style={{ color: 'var(--text-main)' }}>{coin.card_data.trophy_owner_name} {coin.card_data.trophy_owner_lastname || ''}</strong>
                            {coin.card_data.fish_type && ` • ${coin.card_data.fish_type}`}
                          </div>
                        )}
                        {isAllocated && getCoinClaimEmail(coin) && (
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: 'var(--text-mute)' }}>Claim Email:</span>
                            <span style={{ color: 'var(--primary-color, #0284c7)', fontFamily: 'monospace', fontSize: '11px', fontWeight: 600 }}>
                              {getCoinClaimEmail(coin)}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Allocation Status Column */}
                      <td style={{ padding: '12px 16px', minWidth: '140px', whiteSpace: 'nowrap' }}>
                        {isAllocated ? (
                          <span style={{
                            background: 'rgba(16, 185, 129, 0.15)',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            color: '#059669',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}>
                            <Icons.Check size={11} />
                            Allocated
                          </span>
                        ) : (
                          <span style={{
                            background: 'rgba(217, 119, 6, 0.15)',
                            border: '1px solid rgba(217, 119, 6, 0.4)',
                            color: '#d97706',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}>
                            ⏳ Unallocated
                          </span>
                        )}
                      </td>

                      {/* Views Column */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#0284c7', fontWeight: 700 }}>
                          <Icons.Eye size={13} />
                          <span>{coin.views_count || 0}</span>
                        </div>
                        {coin.shared_views_count > 0 && (
                          <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '2px' }}>
                            {coin.shared_views_count} shared
                          </div>
                        )}
                      </td>

                      {/* Shares Column */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#9333ea', fontWeight: 700 }}>
                          <Icons.Share size={13} />
                          <span>{coin.shares_count || 0}</span>
                        </div>
                      </td>

                      {/* Host Clicks Column */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#059669', fontWeight: 700 }}>
                          <Icons.Target size={13} />
                          <span>{coin.host_clicks_count || 0}</span>
                        </div>
                        {coin.booking_clicks_count > 0 && (
                          <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '2px' }}>
                            {coin.booking_clicks_count} book
                          </div>
                        )}
                      </td>

                      {/* Operator Column */}
                      <td style={{ padding: '12px 16px', color: 'var(--text-mute)' }}>
                        <div style={{ color: 'var(--text-main)', fontWeight: 500 }}>{coin.operator_name}</div>
                        {coin.operator_company && (
                          <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '1px' }}>{coin.operator_company}</div>
                        )}
                      </td>

                      {/* Actions Column */}
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          type="button"
                          data-testid={`view-coin-modal-btn-${coin.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDoubleClickCoin(coin);
                          }}
                          title="View 3D Preview & Analytics"
                          style={{
                            background: 'rgba(2, 132, 199, 0.12)',
                            border: '1px solid rgba(2, 132, 199, 0.35)',
                            color: '#0284c7',
                            borderRadius: '6px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          <Icons.Eye size={12} />
                          <span>Preview</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PaginationBar
            currentPage={currentPage}
            totalPages={vaultData.total_pages || Math.max(1, Math.ceil((vaultData.total_filtered !== undefined ? vaultData.total_filtered : (vaultData.total_coins || filteredCoins.length)) / pageSize))}
            pageSize={pageSize}
            totalItems={vaultData.total_filtered !== undefined ? vaultData.total_filtered : (vaultData.total_coins || filteredCoins.length)}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[25, 50, 100]}
            itemName="coins"
          />
        </>
      )}
      </div>

      {/* 5. Detailed Modal Window (Double-Click Triggered) */}
      {selectedCoin && typeof document !== 'undefined' && createPortal(
        <div
          onClick={() => setSelectedCoin(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(5, 8, 16, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 999999,
            boxSizing: 'border-box'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg, #111622)',
              color: 'var(--text-main, #f8fafc)',
              border: '1px solid var(--border-color, rgba(255, 255, 255, 0.14))',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '1060px',
              height: isMobileModal ? '92vh' : 'auto',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.7), 0 0 24px rgba(0, 198, 255, 0.15)',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 22px',
              borderBottom: '1px solid var(--border-color, rgba(255, 255, 255, 0.08))',
              background: 'var(--panel-bg, rgba(255, 255, 255, 0.02))',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-main, #ffffff)' }}>
                  {selectedCoin.is_issued ? `Card #${selectedCoin.coin_number}` : `Coin #${selectedCoin.coin_number} of Order #${selectedCoin.order_id}`}
                </span>
                <code style={{ fontSize: '12px', color: '#38bdf8', background: 'rgba(0, 198, 255, 0.12)', padding: '2px 8px', borderRadius: '4px' }}>
                  {selectedCoin.public_code}
                </code>
                {selectedCoin.is_issued ? (
                  <>
                    <span style={{
                      background: 'rgba(16, 185, 129, 0.15)',
                      border: '1px solid rgba(16, 185, 129, 0.35)',
                      color: '#34d399',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 700
                    }}>
                      ✓ Allocated
                    </span>
                    {getCoinClaimEmail(selectedCoin) && (
                      <span
                        data-testid="vault-modal-claim-email-badge"
                        style={{
                          background: 'rgba(56, 189, 248, 0.12)',
                          border: '1px solid rgba(56, 189, 248, 0.35)',
                          color: '#38bdf8',
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        <Icons.Mail size={12} style={{ color: '#38bdf8' }} />
                        <span style={{ color: 'var(--text-mute, #94a3b8)', fontWeight: 500 }}>Claim Email:</span>
                        <strong style={{ color: 'var(--text-main, #f8fafc)', letterSpacing: '0.2px' }}>{getCoinClaimEmail(selectedCoin)}</strong>
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{
                    background: 'var(--warning-bg, rgba(245, 158, 11, 0.15))',
                    border: '1px solid var(--warning-border, rgba(245, 158, 11, 0.35))',
                    color: 'var(--warning-text, #b45309)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 700
                  }}>
                    ⏳ Unallocated (Minted Blank)
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isAdmin && (
                  <button
                    type="button"
                    data-testid="vault-card-print-export-btn"
                    onClick={() => {
                      setPrintExportError('');
                      setPrintExportSuccess(false);
                      setShowPrintExportModal(true);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15), rgba(202, 138, 4, 0.28))',
                      border: '1px solid rgba(234, 179, 8, 0.45)',
                      color: '#fef08a',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(234, 179, 8, 0.35)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(234, 179, 8, 0.18)'}
                    title="Export high-resolution Epson print PDF"
                  >
                    <Icons.Printer size={13} style={{ color: '#facc15' }} />
                    <span>Export Print (PDF)</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setSelectedCoin(null);
                    setShowPrintExportModal(false);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: 'none',
                    color: '#cbd5e1',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.14)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)'}
                >
                  <Icons.Close size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body: 2 Columns */}
            <div style={{
              display: 'flex',
              flexDirection: isMobileModal ? 'column' : 'row',
              gap: isMobileModal ? '16px' : '20px',
              padding: isMobileModal ? '16px' : '18px 22px',
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: isMobileModal ? 1 : '1 1 auto',
              minHeight: 0
            }}>
              {/* Left Column: 3D Card Flip Preview (Fixed - Does NOT scroll) */}
              <div style={{
                flex: isMobileModal ? '0 0 auto' : '0 0 340px',
                width: isMobileModal ? '100%' : '340px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                background: 'var(--panel-bg, rgba(0, 0, 0, 0.25))',
                border: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
                borderRadius: '12px',
                padding: '16px 10px',
                boxSizing: 'border-box',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                <VaultCardPreview
                  coin={selectedCoin}
                  isFlipped={isFlipped}
                  onFlip={() => setIsFlipped(!isFlipped)}
                  user={user}
                  tenantConfig={tenantConfig}
                  isAdmin={isAdmin}
                  onPrintExport={() => {
                    setPrintExportError('');
                    setPrintExportSuccess(false);
                    setShowPrintExportModal(true);
                  }}
                />

                {selectedCoin.is_issued && (
                  <div
                    data-testid="vault-modal-claim-email-card"
                    style={{
                      marginTop: '14px',
                      width: '100%',
                      maxWidth: '320px',
                      background: 'var(--input-bg, rgba(255, 255, 255, 0.035))',
                      border: '1px solid var(--border-color, rgba(255, 255, 255, 0.09))',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}
                  >
                    <div style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <Icons.Mail size={11} style={{ color: '#38bdf8' }} />
                      <span>Claim Email</span>
                    </div>
                    <div style={{
                      fontSize: '12.5px',
                      color: getCoinClaimEmail(selectedCoin) ? '#38bdf8' : '#64748b',
                      fontWeight: 600,
                      wordBreak: 'break-all',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px'
                    }}>
                      <span>{getCoinClaimEmail(selectedCoin) || 'No claim email specified'}</span>
                      {getCoinClaimEmail(selectedCoin) && (
                        <button
                          type="button"
                          onClick={() => handleCopy(getCoinClaimEmail(selectedCoin), 'claim_email_copy')}
                          title="Copy Claim Email"
                          style={{
                            background: copiedCode === 'claim_email_copy' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.15)',
                            border: `1px solid ${copiedCode === 'claim_email_copy' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(56, 189, 248, 0.3)'}`,
                            color: copiedCode === 'claim_email_copy' ? '#34d399' : '#38bdf8',
                            borderRadius: '4px',
                            padding: '2px 7px',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0,
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {copiedCode === 'claim_email_copy' ? <Icons.Check size={11} /> : <Icons.Copy size={11} />}
                          <span>{copiedCode === 'claim_email_copy' ? 'Copied' : 'Copy'}</span>
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', lineHeight: 1.3 }}>
                      Trophy owner address used for claimant verification when claiming this card.
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Detailed Analytics & Event Log */}
              <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                height: isMobileModal ? 'auto' : '100%',
                minHeight: 0,
                overflow: isMobileModal ? 'visible' : 'hidden'
              }}>
                {selectedCoin.is_issued ? (
                  /* Allocated: Full Analytics & Event Log */
                  <>
                    {/* KPI Badges Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', flexShrink: 0 }}>
                      <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.22)', borderRadius: '8px', padding: '8px 10px' }}>
                        <div style={{ fontSize: '10px', color: '#7dd3fc', fontWeight: 700, textTransform: 'uppercase' }}>Views</div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#38bdf8', marginTop: '1px' }}>
                          {selectedCoin.views_count || 0}
                        </div>
                        <div style={{ fontSize: '9px', color: '#cbd5e1', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedCoin.shared_views_count || 0} shared visits
                        </div>
                      </div>

                      <div style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.22)', borderRadius: '8px', padding: '8px 10px' }}>
                        <div style={{ fontSize: '10px', color: '#d8b4fe', fontWeight: 700, textTransform: 'uppercase' }}>Shares</div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#c084fc', marginTop: '1px' }}>
                          {selectedCoin.shares_count || 0}
                        </div>
                        <div style={{ fontSize: '9px', color: '#cbd5e1', marginTop: '1px' }}>
                          Export / QR shares
                        </div>
                      </div>

                      <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.22)', borderRadius: '8px', padding: '8px 10px' }}>
                        <div style={{ fontSize: '10px', color: '#6ee7b7', fontWeight: 700, textTransform: 'uppercase' }}>Link Clicks</div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#34d399', marginTop: '1px' }}>
                          {(selectedCoin.booking_clicks_count || 0) + (selectedCoin.website_clicks_count || 0) + (selectedCoin.email_clicks_count || 0)}
                        </div>
                        <div style={{ fontSize: '9px', color: '#cbd5e1', marginTop: '1px' }}>
                          {selectedCoin.booking_clicks_count || 0} bookings
                        </div>
                      </div>

                      <div style={{ background: 'var(--warning-bg, rgba(245, 158, 11, 0.08))', border: '1px solid var(--warning-border, rgba(245, 158, 11, 0.22))', borderRadius: '8px', padding: '8px 10px' }}>
                        <div style={{ fontSize: '10px', color: 'var(--warning-text, #b45309)', fontWeight: 700, textTransform: 'uppercase' }}>Click-Thru %</div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--warning-text, #b45309)', marginTop: '1px' }}>
                          {coinAnalytics?.metrics?.conversion_rate !== undefined ? `${coinAnalytics.metrics.conversion_rate}%` : (
                            (selectedCoin.views_count > 0)
                              ? `${Math.round((((selectedCoin.booking_clicks_count || 0) + (selectedCoin.website_clicks_count || 0) + (selectedCoin.email_clicks_count || 0)) / selectedCoin.views_count) * 1000) / 10}%`
                              : '0%'
                          )}
                        </div>
                        <div style={{ fontSize: '9px', color: 'var(--text-mute, #cbd5e1)', marginTop: '1px' }}>
                          Engagement rate
                        </div>
                      </div>
                    </div>

                    {/* 2. Slick 14-Day Activity & Engagement Trend Graph */}
                    <div style={{
                      background: 'var(--panel-bg, rgba(0, 0, 0, 0.25))',
                      border: '1px solid var(--border-color, rgba(255, 255, 255, 0.08))',
                      borderRadius: '10px',
                      padding: '8px 12px',
                      flexShrink: 0
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-main, #ffffff)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>📈</span>
                          <span>14-Day Activity & Engagement Trend</span>
                        </div>
                        <span style={{ fontSize: '10px', color: 'var(--text-mute, #cbd5e1)' }}>
                          Engagement: <strong style={{ color: '#34d399' }}>{coinAnalytics?.metrics?.conversion_rate !== undefined ? `${coinAnalytics.metrics.conversion_rate}%` : '0%'}</strong>
                        </span>
                      </div>

                      {analyticsLoading ? (
                        <div style={{ height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-mute, #94a3b8)', fontSize: '11px', gap: '6px' }}>
                          <Icons.Activity size={13} />
                          <span>Generating activity trend graph...</span>
                        </div>
                      ) : (
                        <SlickTimelineChart timeline={coinAnalytics?.timeline || []} />
                      )}
                    </div>

                    {/* 3. Traffic Channels & Clicks Summary Bar */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '8px',
                      flexShrink: 0
                    }}>
                      {/* Channels */}
                      <div style={{
                        background: 'var(--input-bg, rgba(255, 255, 255, 0.025))',
                        border: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
                        borderRadius: '8px',
                        padding: '6px 10px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-mute, #cbd5e1)', marginBottom: '3px' }}>
                          <span style={{ fontWeight: 600 }}>Traffic Channel Mix</span>
                          <span style={{ color: 'var(--text-main, #f1f5f9)' }}>
                            <strong style={{ color: '#00c6ff' }}>{Math.max(0, (selectedCoin.views_count || 0) - (selectedCoin.shared_views_count || 0))}</strong> Direct vs <strong style={{ color: '#a855f7' }}>{selectedCoin.shared_views_count || 0}</strong> Shared
                          </span>
                        </div>
                        <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden', display: 'flex' }}>
                          <div style={{
                            width: `${selectedCoin.views_count > 0 ? (Math.max(0, (selectedCoin.views_count || 0) - (selectedCoin.shared_views_count || 0)) / selectedCoin.views_count) * 100 : 0}%`,
                            height: '100%',
                            background: '#00c6ff'
                          }} />
                          <div style={{
                            width: `${selectedCoin.views_count > 0 ? ((selectedCoin.shared_views_count || 0) / selectedCoin.views_count) * 100 : 0}%`,
                            height: '100%',
                            background: '#a855f7'
                          }} />
                        </div>
                      </div>

                      {/* Clicks */}
                      <div style={{
                        background: 'var(--input-bg, rgba(255, 255, 255, 0.025))',
                        border: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
                        borderRadius: '8px',
                        padding: '6px 10px'
                      }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-mute, #cbd5e1)', fontWeight: 600, marginBottom: '2px' }}>
                          Host Link Engagement
                        </div>
                        <div style={{ display: 'flex', gap: '10px', fontSize: '10px' }}>
                          <span style={{ color: 'var(--text-mute, #cbd5e1)' }}>Bookings: <strong style={{ color: '#10b981' }}>{selectedCoin.booking_clicks_count || 0}</strong></span>
                          <span style={{ color: 'var(--text-mute, #cbd5e1)' }}>Website: <strong style={{ color: '#38bdf8' }}>{selectedCoin.website_clicks_count || 0}</strong></span>
                          <span style={{ color: 'var(--text-mute, #cbd5e1)' }}>Email: <strong style={{ color: '#f59e0b' }}>{selectedCoin.email_clicks_count || 0}</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* 4. Views & Click-Through Log Panel (Compact & Scrollable) */}
                    <div style={{
                      flex: 1,
                      minHeight: isMobileModal ? '180px' : '100px',
                      maxHeight: isMobileModal ? 'none' : '220px',
                      background: 'var(--panel-bg, rgba(0, 0, 0, 0.25))',
                      border: '1px solid var(--border-color, rgba(255, 255, 255, 0.08))',
                      borderRadius: '10px',
                      padding: '8px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}>
                      {/* Log Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                        flexWrap: 'wrap',
                        gap: '6px',
                        flexShrink: 0
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icons.Activity size={13} style={{ color: '#38bdf8' }} />
                          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-main, #ffffff)' }}>
                            Views & Clicks Log
                          </span>
                          <span style={{
                            background: 'rgba(56, 189, 248, 0.12)',
                            color: '#38bdf8',
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: '10px'
                          }}>
                            {filteredLogs.length}
                          </span>
                        </div>

                        {/* Filter Chips */}
                        <div style={{ display: 'flex', gap: '3px', background: 'rgba(255, 255, 255, 0.04)', padding: '2px', borderRadius: '6px' }}>
                          {[
                            { key: 'all', label: `All (${logCounts.all})` },
                            { key: 'views', label: `Views (${logCounts.views})` },
                            { key: 'clicks', label: `Clicks (${logCounts.clicks})` },
                            { key: 'shares', label: `Shares (${logCounts.shares})` },
                          ].map(tab => (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setLogFilter(tab.key)}
                              style={{
                                background: logFilter === tab.key ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                                border: 'none',
                                color: logFilter === tab.key ? '#38bdf8' : '#cbd5e1',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '9px',
                                fontWeight: logFilter === tab.key ? 700 : 600,
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Scrollable Events List */}
                      <div style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        paddingRight: '4px'
                      }}>
                        {analyticsLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '11px', gap: '6px' }}>
                            <Icons.Activity size={13} />
                            <span>Loading engagement events...</span>
                          </div>
                        ) : filteredLogs.length === 0 ? (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#94a3b8',
                            padding: '12px',
                            textAlign: 'center',
                            gap: '4px'
                          }}>
                            <Icons.Eye size={18} style={{ opacity: 0.6 }} />
                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#cbd5e1' }}>No recorded events in this category</span>
                            <span style={{ fontSize: '10px', color: '#94a3b8', maxWidth: '280px' }}>
                              When viewers scan the physical coin NFC tag or click card back links, interactions will log here live.
                            </span>
                          </div>
                        ) : (
                          filteredLogs.map((item, idx) => {
                            const meta = getLogEventMeta(item.event_type);
                            const IconComp = meta.icon;
                            return (
                              <div
                                key={item.id || idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  background: 'rgba(255, 255, 255, 0.02)',
                                  border: '1px solid rgba(255, 255, 255, 0.05)',
                                  borderRadius: '5px',
                                  padding: '5px 8px',
                                  gap: '8px',
                                  transition: 'background 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'}
                              >
                                {/* Left: Icon & Description */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                                  <div style={{
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '5px',
                                    background: meta.bg,
                                    color: meta.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}>
                                    <IconComp size={11} />
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                    <div style={{ fontSize: '10.5px', fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.label || meta.badge}
                                    </div>
                                    <div style={{ fontSize: '9.5px', color: '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.destination_url || item.source || meta.defaultDesc}
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Category badge & relative time */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                  <span style={{
                                    fontSize: '8.5px',
                                    fontWeight: 700,
                                    color: meta.badgeColor,
                                    background: meta.badgeBg,
                                    padding: '1px 5px',
                                    borderRadius: '3px'
                                  }}>
                                    {meta.badge}
                                  </span>
                                  <span
                                    title={item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                                    style={{ fontSize: '9.5px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px' }}
                                  >
                                    <Icons.Clock size={9} />
                                    <span>{formatEventTime(item.timestamp)}</span>
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Actions Row */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '2px', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => handleCopy(selectedCoin.rfid_url, 'modal_link')}
                        style={{
                          flex: 1,
                          background: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          color: '#fff',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {copiedCode === 'modal_link' ? <Icons.Check size={14} /> : <Icons.Copy size={14} />}
                        <span>{copiedCode === 'modal_link' ? 'Link Copied!' : 'Copy Coin URL'}</span>
                      </button>

                      <a
                        href={selectedCoin.rfid_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          flex: 1.5,
                          background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                          color: '#fff',
                          textDecoration: 'none',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>Open Live Coin</span>
                        <Icons.ExternalLink size={13} />
                      </a>
                    </div>
                  </>
                ) : (
                  /* Unallocated: Clean Template Info & Issuance Action */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center', height: '100%' }}>
                    <div style={{
                      background: 'var(--warning-bg, rgba(245, 158, 11, 0.08))',
                      border: '1px solid var(--warning-border, rgba(245, 158, 11, 0.3))',
                      borderRadius: '10px',
                      padding: '16px',
                      color: 'var(--text-main, #1e293b)',
                      fontSize: '12px',
                      lineHeight: '1.6'
                    }}>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--warning-text, #b45309)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⏳</span>
                        <span>Unallocated Coin (Blank Minted)</span>
                      </div>
                      This coin was minted as part of <strong>Order #{selectedCoin.order_id}</strong> and is currently blank in the vault.
                      The preview on the left demonstrates the clean trophy card template before personalization.
                    </div>

                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      fontSize: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#cbd5e1' }}>Token Code:</span>
                        <code style={{ color: '#38bdf8' }}>{selectedCoin.public_code}</code>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#cbd5e1' }}>Coin Number:</span>
                        <span style={{ color: '#ffffff', fontWeight: 600 }}>#{selectedCoin.coin_number}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#cbd5e1' }}>Order ID:</span>
                        <span style={{ color: '#ffffff', fontWeight: 600 }}>Order #{selectedCoin.order_id}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#cbd5e1' }}>Minted Date:</span>
                        <span style={{ color: '#f8fafc' }}>{selectedCoin.minted_at ? new Date(selectedCoin.minted_at).toLocaleDateString() : 'Active'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#cbd5e1' }}>Card Template:</span>
                        <span style={{ color: '#ffffff', fontWeight: 600 }}>{selectedCoin.card_name}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#cbd5e1' }}>NFC / RFID URL:</span>
                        <span style={{ color: '#cbd5e1', fontSize: '11px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedCoin.rfid_url}
                        </span>
                      </div>
                    </div>

                    {/* Primary Issue Button */}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={() => handleCopy(selectedCoin.rfid_url, 'unallocated_link')}
                        style={{
                          flex: 1,
                          background: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          color: '#fff',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {copiedCode === 'unallocated_link' ? <Icons.Check size={14} /> : <Icons.Copy size={14} />}
                        <span>{copiedCode === 'unallocated_link' ? 'Link Copied!' : 'Copy NFC URL'}</span>
                      </button>

                      <a
                        href={selectedCoin.rfid_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          flex: 1.5,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: '#fff',
                          textDecoration: 'none',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)'
                        }}
                      >
                        <span>Personalize & Issue Coin</span>
                        <Icons.ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Staging container for clean unscaled high-resolution print export */}
      {selectedCoin && isAdmin && (
        <div
          id={`vault-print-staging-${selectedCoin.id}`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: `${(selectedCoin?.card_template_snapshot?.config?.cardDimensions || selectedCoin?.card_template_snapshot?.cardDimensions || { width: 400, height: 560 }).width || 400}px`,
            height: `${(selectedCoin?.card_template_snapshot?.config?.cardDimensions || selectedCoin?.card_template_snapshot?.cardDimensions || { width: 400, height: 560 }).height || 560}px`,
            pointerEvents: 'none',
            zIndex: -9999,
            opacity: 0.001,
            overflow: 'hidden'
          }}
          aria-hidden="true"
        >
          <div
            id={`vault-print-clean-front-${selectedCoin.id}`}
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              boxShadow: 'none'
            }}
          >
            <BragCardCanvas
              design={selectedCoin?.card_template_snapshot || {}}
              side="front"
              testDataOverrides={selectedCoin?.is_issued ? (selectedCoin?.card_data || {}) : {}}
              datasets={{}}
              user={user}
              tenantConfig={tenantConfig}
              id={`vault-print-canvas-front-${selectedCoin.id}`}
              style={{ boxShadow: 'none' }}
            />
          </div>
          <div
            id={`vault-print-clean-back-${selectedCoin.id}`}
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              boxShadow: 'none'
            }}
          >
            <BragCardCanvas
              design={selectedCoin?.card_template_snapshot || {}}
              side="back"
              testDataOverrides={selectedCoin?.is_issued ? (selectedCoin?.card_data || {}) : {}}
              datasets={{}}
              user={user}
              tenantConfig={tenantConfig}
              id={`vault-print-canvas-back-${selectedCoin.id}`}
              style={{ boxShadow: 'none' }}
            />
          </div>
        </div>
      )}

      {/* Print Export Modal */}
      {renderPrintExportModal()}
    </div>
  );
}
